/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

/**
 * Maps a language code to the flag it should be displayed with, where the
 * mapping is ambiguous (a language does not map 1:1 to a single country
 * flag). Codes not listed here use themselves as the flag code.
 */
const AMBIGUOUS_FLAG_BY_LANGUAGE: Record<string, string> = {
  en: 'gb',
  cs: 'cz',
  da: 'dk',
  ja: 'jp',
  sv: 'se',
  zh: 'cn',
};

/**
 * Closed allowlist of language codes for which a local `flag-icons` SVG is
 * bundled: the platform set (`cs da de en es fr it ja pt sv zh`) plus the
 * `machinery-1.2.0` schema-declared codes (`de en fr`, already covered by
 * the platform set).
 *
 * This is also the single source of truth for the platform's fixed language
 * set used elsewhere (e.g. the Editor's `list:language` language select) —
 * do not introduce a second, independently-maintained list of codes.
 */
export const PLATFORM_LANGUAGE_CODES = new Set([
  'cs',
  'da',
  'de',
  'en',
  'es',
  'fr',
  'it',
  'ja',
  'pt',
  'sv',
  'zh',
]);

/**
 * Resolves the local, build-time-bundled flag asset path for a language
 * code. This is the only place in the webapp a flag path is built — no
 * remote host (e.g. `flagcdn.com`) is ever used.
 *
 * @param languageCode - Language code to resolve a flag for (for example: en, de).
 * @returns The local flag asset path, or `null` if the code is outside the allowlist —
 *   in that case, the caller renders the uppercase language code as text instead.
 */
export function flagAssetPath(
  languageCode: string | undefined | null,
): string | null {
  if (!languageCode || !PLATFORM_LANGUAGE_CODES.has(languageCode)) {
    return null;
  }
  const flagCode = AMBIGUOUS_FLAG_BY_LANGUAGE[languageCode] ?? languageCode;
  return `/flags/${flagCode}.svg`;
}
